import React, { useState } from 'react';
import axios from 'axios';
import { Bell, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PushPrompt = ({ onComplete }) => {
  const [loading, setLoading] = useState(false);

  const handleEnable = async () => {
    if (!('Notification' in window) || !('serviceWorker' in navigator)) {
      toast.error('Your browser does not support push notifications');
      onComplete();
      return;
    }

    setLoading(true);
    try {
      const permission = await Notification.requestPermission();

      if (permission === 'granted') {
        const registration = await navigator.serviceWorker.register('/sw.js');
        await navigator.serviceWorker.ready;

        const vapidRes = await axios.get(`${API}/notifications/vapid-key`);
        const vapidPublicKey = vapidRes.data.publicKey;

        const padding = '='.repeat((4 - vapidPublicKey.length % 4) % 4);
        const base64 = (vapidPublicKey + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);

        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: outputArray,
        });

        await axios.post(`${API}/notifications/subscribe`, subscription.toJSON());
        toast.success('Notifications enabled!');
      } else {
        toast.info('You can enable notifications later in Settings');
      }
    } catch (error) {
      /* silent */
      toast.error('Could not enable notifications. Try again in Settings.');
    } finally {
      setLoading(false);
      onComplete();
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20,
    }}>
      <div style={{
        background: '#1a2332', border: '1px solid rgba(201,169,78,0.3)',
        borderRadius: 20, padding: 32, maxWidth: 380, width: '100%',
        textAlign: 'center',
      }}>
        <div style={{
          width: 64, height: 64, borderRadius: '50%',
          background: 'rgba(15,59,46,0.4)', border: '2px solid rgba(47,143,107,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 20px',
        }}>
          <Bell size={28} color="#2F8F6B" />
        </div>

        <h2 style={{
          fontFamily: 'var(--font-serif, Georgia, serif)',
          fontSize: 22, fontWeight: 700, color: '#f5f0e8',
          marginBottom: 10,
        }}>
          Stay in the Loop
        </h2>

        <p style={{
          color: '#94a3b8', fontSize: 14, lineHeight: 1.6,
          marginBottom: 24,
        }}>
          Get notified when you match with a playing partner, receive messages, or someone interacts with your posts.
        </p>

        <button
          onClick={handleEnable}
          disabled={loading}
          data-testid="push-prompt-enable"
          style={{
            width: '100%', padding: '14px 0', borderRadius: 12,
            background: '#0F3B2E', color: '#f5f0e8',
            fontSize: 15, fontWeight: 600, border: 'none',
            cursor: loading ? 'wait' : 'pointer',
            marginBottom: 12,
          }}
        >
          {loading ? 'Setting up...' : 'Enable Notifications'}
        </button>

        <button
          onClick={onComplete}
          data-testid="push-prompt-skip"
          style={{
            width: '100%', padding: '10px 0', borderRadius: 12,
            background: 'transparent', color: '#64748b',
            fontSize: 13, fontWeight: 500, border: 'none',
            cursor: 'pointer',
          }}
        >
          Not now
        </button>
      </div>
    </div>
  );
};

export default PushPrompt;
